import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Linear/Vercel-style keyboard list navigation.
 *
 *   j  / ↓     next row
 *   k  / ↑     prev row
 *   Enter      onEnter(activeItem)
 *   g g        jump to first row (vim-style; press g twice within 800ms)
 *   G          jump to last row
 *   Esc        clear active selection
 *   Tab        cycle to next registered section (when wrapped in <NavigationProvider>)
 *   Shift+Tab  cycle to previous registered section
 *
 * Skips when user is focused in <input>, <textarea>, [contenteditable], or
 * inside the global Cmd Palette.
 *
 * SINGLE-LIST USAGE (no provider):
 *   const { getRowProps } = useListNavigation(items, onEnter);
 *   <li {...getRowProps(i)} />
 *
 * MULTI-LIST USAGE (per-section focus + Tab cycling):
 *   <NavigationProvider>
 *     <Section sectionId="decisions"> ... </Section>
 *     <Section sectionId="routing">  ... </Section>
 *   </NavigationProvider>
 *
 *   function Section({ sectionId }) {
 *     const { getRowProps, sectionProps } = useListNavigation(items, onEnter, { sectionId });
 *     return <div {...sectionProps}><ul>{items.map(...)}</ul></div>;
 *   }
 *
 * Active section is the one currently focused (last hovered or Tab-selected).
 * Only the active section listens to j/k/Enter/g/G — prevents conflicts.
 */

interface NavigationCtx {
  activeSection: string | null;
  setActiveSection: (id: string | null) => void;
  registerSection: (id: string) => void;
  unregisterSection: (id: string) => void;
  cycleSection: (delta: 1 | -1) => void;
}

const NavigationContext = createContext<NavigationCtx | null>(null);

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const sectionsRef = useRef<string[]>([]);

  const registerSection = useCallback((id: string) => {
    if (!sectionsRef.current.includes(id)) {
      sectionsRef.current = [...sectionsRef.current, id];
    }
  }, []);

  const unregisterSection = useCallback((id: string) => {
    sectionsRef.current = sectionsRef.current.filter((s) => s !== id);
  }, []);

  const cycleSection = useCallback(
    (delta: 1 | -1) => {
      const list = sectionsRef.current;
      if (list.length === 0) return;
      const idx = activeSection ? list.indexOf(activeSection) : -1;
      const next = idx < 0 ? 0 : (idx + delta + list.length) % list.length;
      setActiveSection(list[next]);
    },
    [activeSection]
  );

  // Tab / Shift+Tab cycles sections at document level
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = document.activeElement;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        if (target.isContentEditable) return;
        if (target.closest("[cmdk-root]")) return;
      }
      if (sectionsRef.current.length < 2) return;
      e.preventDefault();
      cycleSection(e.shiftKey ? -1 : 1);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cycleSection]);

  const value = useMemo<NavigationCtx>(
    () => ({
      activeSection,
      setActiveSection,
      registerSection,
      unregisterSection,
      cycleSection,
    }),
    [activeSection, registerSection, unregisterSection, cycleSection]
  );

  return (
    <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
  );
}

export function useListNavigation<T>(
  items: T[],
  onEnter: (item: T, index: number) => void,
  opts: { enabled?: boolean; sectionId?: string } = {}
) {
  const ctx = useContext(NavigationContext); // null if no provider
  const { sectionId } = opts;
  const enabledExplicit = opts.enabled;

  // Determine if this hook should be the one currently listening:
  //   - With provider + sectionId: only when this is the active section
  //   - Without provider: always (single-list mode)
  //   - enabled flag overrides if explicitly false
  const enabled =
    enabledExplicit === false
      ? false
      : sectionId && ctx
      ? ctx.activeSection === sectionId
      : true;

  const [activeIndex, setActiveIndex] = useState(-1);
  const itemsRef = useRef(items);
  const onEnterRef = useRef(onEnter);
  const lastGAt = useRef<number>(0);
  const rowRefs = useRef<Map<number, HTMLElement>>(new Map());

  itemsRef.current = items;
  onEnterRef.current = onEnter;

  // Register/unregister with NavigationProvider
  useEffect(() => {
    if (!ctx || !sectionId) return;
    ctx.registerSection(sectionId);
    return () => ctx.unregisterSection(sectionId);
  }, [ctx, sectionId]);

  useEffect(() => {
    if (!enabled) return;

    function isTextField(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      if (el.closest("[cmdk-root]")) return true;
      return false;
    }

    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTextField(document.activeElement)) return;
      const len = itemsRef.current.length;
      if (len === 0) return;

      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((idx) => Math.min(len - 1, idx < 0 ? 0 : idx + 1));
        return;
      }
      if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((idx) => Math.max(0, idx < 0 ? 0 : idx - 1));
        return;
      }
      if (e.key === "Enter") {
        const idx = activeIndex;
        if (idx >= 0 && idx < len) {
          e.preventDefault();
          onEnterRef.current(itemsRef.current[idx], idx);
        }
        return;
      }
      if (e.key === "g") {
        const now = Date.now();
        if (now - lastGAt.current < 800) {
          e.preventDefault();
          setActiveIndex(0);
          lastGAt.current = 0;
        } else {
          lastGAt.current = now;
        }
        return;
      }
      if (e.key === "G") {
        e.preventDefault();
        setActiveIndex(len - 1);
        return;
      }
      if (e.key === "Escape") {
        setActiveIndex(-1);
        return;
      }
    }

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled, activeIndex]);

  // Auto-scroll active row into view when it changes
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = rowRefs.current.get(activeIndex);
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIndex]);

  // Reset active index when items shape changes (filter applied, fresh data)
  useEffect(() => {
    if (activeIndex >= items.length) setActiveIndex(-1);
  }, [items.length, activeIndex]);

  // Clear local active when this section loses focus
  useEffect(() => {
    if (!enabled) setActiveIndex(-1);
  }, [enabled]);

  const getRowProps = useCallback(
    (i: number) => ({
      ref: (el: HTMLElement | null) => {
        if (el) rowRefs.current.set(i, el);
        else rowRefs.current.delete(i);
      },
      "data-active": i === activeIndex && enabled ? "true" : undefined,
      onMouseEnter: () => setActiveIndex(i),
    }),
    [activeIndex, enabled]
  );

  // Section container props — sets this section active on hover/focus,
  // applies a subtle ring when active.
  const sectionProps = useMemo(
    () =>
      sectionId && ctx
        ? {
            "data-nav-section": sectionId,
            "data-nav-active": ctx.activeSection === sectionId ? "true" : undefined,
            onMouseEnter: () => ctx.setActiveSection(sectionId),
            onFocusCapture: () => ctx.setActiveSection(sectionId),
          }
        : {},
    [sectionId, ctx]
  );

  return { activeIndex, setActiveIndex, getRowProps, sectionProps };
}
